import { useState, useEffect } from "react";

/**
 * Like useState, but persists the value to localStorage under the given key.
 * Falls back to `defaultValue` when nothing is stored.
 *
 * Reads localStorage synchronously in the initializer to avoid a flash of
 * default content (e.g., showing "upcoming" meetings before switching to "today").
 * This is safe because the app is a SPA — no SSR hydration mismatch concern.
 *
 * NOTE: only for global / per-user state. For anything scoped to the active
 * workspace use `useWorkspaceLocalStorage` instead — this hook reads once at
 * mount and never re-reads when the key changes, so on the reactive (no-reload)
 * workspace switch it would keep the previous workspace's value (even if you
 * interpolate the workspace id into the key). See LOVABLE.md #23.
 */
export function useStickyState<T>(key: string, defaultValue: T): [T, (val: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw !== null) {
        return JSON.parse(raw);
      }
    } catch {
      // Ignore errors (e.g., invalid JSON, quota exceeded)
    }
    return defaultValue;
  });

  // Persist to localStorage whenever value changes
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Ignore errors (e.g., quota exceeded, private browsing)
    }
  }, [key, value]);

  return [value, setValue];
}
